/** @module Auth0SentryHelper */
/* eslint-disable import/extensions */
import logger from './auth0-logger.mjs';

const MIN_SENTRY_VERSION = 8;

/**
 * @description A small wrapper that allows the Auth0 NPM package to use an app's sentry instance without mucking up that app's own scope and context (sentry hates it when we try to make a new hub / scope after the app has already initialized its own). Currently does not expect to initialize Sentry (maybe later??), but rather requires the consuming app of `auth0-helpers` package provide its own already initialized Sentry instance. This class will ensure that any users or context set for Sentry are isolated to the single events it sends. REQUIRES SENTRY v8 OR BETTER!
 */
class Auth0SentryHelper {
  // REQUIRES SENTRY v8 OR BETTER!
  constructor(options) {
    this._sentry = options.sentry;
    this._baseIndividualTags = {};

    console.log('Auth0SentryHelper init with Sentry client version', this._sentry.SDK_VERSION);
    this._validate();
  }

  _validate = () => {
    if (!this._sentry) {
      throw new Error('Auth0SentryHelper failed to initialize because no sentry client was provided in `options.sentry`');
    }

    try {
      const regex = /^(\d+)\.(\d+)\.(\d+)$/;
      const version = this._sentry.SDK_VERSION;
      const match = version.match(regex);

      if (!match) {
        throw new Error(`Auth0SentryHelper failed to initialize because the provided sentry client has an unknown version. Version ${MIN_SENTRY_VERSION}+ is required and version ${version} was detected.`);
      }

      const major = match[1];
      if (parseInt(major, 10) < MIN_SENTRY_VERSION) {
        throw new Error(`Auth0SentryHelper failed to initialize because the provided sentry client is too old. Version ${MIN_SENTRY_VERSION}+ is required and version ${version} was detected.`);
      }
    } catch (error) {
      throw new Error('Auth0SentryHelper failed to initialize because sentry client validation failed.', error);
    }
  };

  /**
   * @function
   * @param {object} e The error / exception object to capture
   * @param {object} [localizedContextData] Optional localized context. If provided, it will be merged with any context set previously with `setUser()`, `setContext()`, `setTags()` or `addTag()`.
   * @param {object} [localizedContextData.user] A localized user or overrides to the user specified using `setUser`.
   * @param {string} [localizedContextData.user.id] The user's ID
   * @param {string} [localizedContextData.user.username] The user's username
   * @param {string} [localizedContextData.user.email] The user's email
   * @param {string} [localizedContextData.user.ip_address] The user's IP address
   * @param {string} [localizedContextData.contextName] A localized context name (will override any contextName set using `setContext()`).
   * @param {object} [localizedContextData.context] The localized context object. Can be any shape and include any relevant data. If the Auth0SentryHelper.setContext() method has been used, this prop and the base context will be merged and local context will have precedence. This will be included in the Sentry event
   * @param {object} [localizedContextData.tags] A simple dictionary object with string values. These will be attached to the event as tags and have precedence over any tags set with `setTags()` or `addTag()`.
   */
  captureException = (e, localizedContextData = {}) => {
    if (!this._sentry) {
      logger.warn(`Auth0SentryHelper.captureException with exception "${e}" has failed because no Sentry object has been provided.`);
      return;
    }

    this._sentry.withScope((scope) => {
      this._setContext(localizedContextData, scope);
      this._sentry.captureException(e);
    });
  };

  /**
   * @function
   * @param {string} msg The message to capture (in a Sentry event)
   * @param {string} [eventLevel='info'] The sentry-specific event level, Can be "fatal", "error", "warning", "log", "info", and "debug"
   * @param {object} [localizedContextData] Optional localized context. If provided, it will be merged with any context set previously with `setUser()`, `setContext()`, `setTags()` or `addTag()`.
   * @param {object} [localizedContextData.user] A localized user or overrides to the user specified using `setUser`.
   * @param {string} [localizedContextData.user.id] The user's ID
   * @param {string} [localizedContextData.user.username] The user's username
   * @param {string} [localizedContextData.user.email] The user's email
   * @param {string} [localizedContextData.user.ip_address] The user's IP address
   * @param {string} [localizedContextData.contextName] A localized context name (will override any contextName set using `setContext()`).
   * @param {object} [localizedContextData.context] The localized context object. Can be any shape and include any relevant data. If the Auth0SentryHelper.setContext() method has been used, this prop and the base context will be merged and local context will have precedence. This will be included in the Sentry event
   * @param {object} [localizedContextData.tags] A simple dictionary object with string values. These will be attached to the event as tags and have precedence over any tags set with `setTags()` or `addTag()`.
   */
  captureMessage = (msg, eventLevel = 'info', localizedContextData = {}) => {
    if (!this._sentry) {
      logger.warn(`Auth0SentryHelper.captureMessage with message "${msg}" has failed because no Sentry object has been provided.`);
      return;
    }

    this._sentry.withScope((scope) => {
      this._setContext(localizedContextData, scope);
      this._sentry.captureMessage(msg, eventLevel);
    });
  };

  /**
   * @description This function allows callers to set a general base context that applies to all future capture events. Capture methods also allow for inclusion of additional localized context that will be merged with this base object to provide the full context object
   * @param {string} contextName Each context in a sentry event has a descriptive title (e.g. "createUser", etc)
   * @param {object} context The base content object. Any shape is OK.
   */
  setContext = (contextName, context = {}) => {
    this._baseContextName = contextName;
    this._baseContext = context;
  };

  clearContext = () => {
    this._baseContextName = null;
    this._baseContext = null;
  };

  /**
   * @description This function allows callers to set a user that applies to all future capture events. Capture methods also allow for inclusion of additional localized user data that will be merged with this base user to provide a user for the event
   * @param {object} user The base content object. Any shape is OK.
   * @param {string} [user.id] The user's ID
   * @param {string} [user.username] The user's username
   * @param {string} [user.email] The user's email
   * @param {string} [user.ip_address] The user's IP address
   */
  setUser = (user) => {
    this._baseUser = user;
  };

  /**
   * @description This function allows callers clear the user added earlier with the `setUser()` function.
   */
  clearUser = () => {
    this._baseUser = null;
  };

  /**
   * @description This function allows callers to specify a sey of tags that apply to all future capture events. Capture methods also allow for inclusion of additional localized user data that will be merged with this base user to provide a user for the event
   * @param {object} tags A simple dictionary object with string values. These will be attached to the event as tags.
   */
  setTags = (tags) => {
    this._baseTags = tags;
  };

  /**
   * @description This function allows callers clear the tag set added earlier with the `setTags()` function.
   */
  clearTags = () => {
    this._baseTags = null;
  };

  /**
   * @description This function allows callers to add (or replace) an individual tag within the event tag set. This tag will be present in all future capture events.
   * @param {string} key The name (key) of the new tag
   */
  addTag = (key, value) => {
    this._baseIndividualTags[key] = value;
  };

  /**
   * @description This function allows callers clear a specific tag that was added earlier with the `addTag()` function.
   * @param {object} key The tag ket to remove
   */
  clearTag = (key) => {
    delete this._baseIndividualTags[key];
  };

  /**
   * @private
   * @function
   * @description Sets context for Sentry events, but does it in a way that will not alert the providing app's own context settings, THIS FUNCTION IS MEANT TO BE CALLED in `captureException` and `captureMessage` ONLY!!
   * @param {object} localizedContextData The data to use for the temporary Sentry context
   * @param {object} [localizedContextData.user] Will set the sentry event's user
   * @param {string} [sentryData.user.id] The user's ID
   * @param {string} [sentryData.user.username] The user's username
   * @param {string} [sentryData.user.email] The user's email
   * @param {string} [sentryData.user.ip_address] The user's IP address
   * @param {object} [localizedContextData.contextName] Each context in a sentry event has a descriptive title (e.g. "createUser", etc)
   * @param {object} [localizedContextData.context] The localized context object. Can be any shape and include any relevant data. If the Auth0SentryHelper.setContext() method has been used, `context` and the base context will be merged and local context will have precedence. This will be included in the Sentry event
   * @param {object} [localizedContextData.tags] A simple dictionary object with string values. These will be attached to the event as tags.
   * @param {object} sentryScope The actual Sentry Scope object
   */
  _setContext = (localizedContextData, sentryScope) => {
    // this function is responsible for merging localized context with "base" context
    // THIS FUNCTION IS MEANT TO BE CALLED in `captureException` and `captureMessage` ONLY!! Calling outside of those function runs the risk of polluting the providing app's own context
    if (!sentryScope) {
      throw new Error('Auth0SentryHelper: invalid sentryScope');
    }

    const builtUser = this._buildUser(localizedContextData.user);
    if (builtUser) {
      sentryScope.setUser(builtUser);
    }

    const builtContext = this._buildContext(localizedContextData);
    if (builtContext) {
      sentryScope.setContext(builtContext.contextName, builtContext.context);
    }

    const builtTagSet = this._buildTags(localizedContextData.tags);
    if (builtTagSet) {
      Object.keys(builtTagSet).forEach(tagKey => sentryScope.setTag(tagKey, builtTagSet.tags[tagKey]));
    }

    sentryScope.setTag('auth0-helpers-version', this._version);
  };

  _buildUser = (localUserData = null) => {
    if (localUserData && typeof localUserData !== 'object') {
      throw new Error('Cannot build Sentry user, localContextData must be an object.');
    }
    const baseUser = this._baseUser ?? {};
    localUserData = localUserData ?? {};
    return {
      ...baseUser,
      ...localUserData,
    };
  };

  _buildContext = (localContext = null) => {
    if (localContext && typeof localContext !== 'object') {
      throw new Error('Cannot build Sentry context, localContext must be an object.');
    }
    const contextName = this._baseContextName || localContext?.contextName || 'untitledContext';
    const baseContext = this._baseContext ?? {};
    localContext = localContext?.context ?? {};
    return {
      contextName,
      context: {
        ...baseContext,
        ...localContext,
      },
    };
  };

  _buildTags = (localTags = null) => {
    if (localTags && typeof localTags !== 'object') {
      throw new Error('Cannot build Sentry tag set, localTags must be an object.');
    }
    const baseTagSet = this._baseTags ?? {};
    localTags = localTags ?? {};
    const mergedTagSet = {
      ...baseTagSet,
      ...localTags,
    };

    Object.keys(this._baseIndividualTags).forEach(tagKey => mergedTagSet[tagKey] = this._baseIndividualTags[tagKey]);
  };

  // DO NOT REMOVE; MUSTACHE VAR OVERRIDE; 
  get _version() {
    return '71c5d67';
  }
}

export default Auth0SentryHelper;
